import React from 'react';
import PropTypes from 'prop-types';
import xmlDomUtils from '../../../qti/utils/XmlDomUtils';
import interactionParserUtils from '../../../qti/parser/question/interactions/InteractionParserUtils';
import OutcomeIcon from './../interactionOutcome/OutcomeIcon';
import { showMedia, unmountMedia } from '../media/MediaDisplay';
import { t } from '../../../services/i18nService';
import domService from '../../../services/DomService';

import styles from '../QuestionDisplay.scss';

const defaultState = {
    expanded: true,
};

class FeedbackForAlternative extends React.PureComponent {
    constructor(props, context) {
        super(props, context);
        this.state = defaultState;
    }

    /**
     * We'll look for soon-to-be-media in our rendered html and let the media renderer it properly.
     */
    componentDidMount() {
        const { services } = this.props;

        const showMediaParams = {
            services,
            displayContext: 'choiceOption',
        };

        const promises = this.getMediaModels()
            .map((mediaModel) => {
                const target = this.convertMediaId(mediaModel.target);
                const model = { ...mediaModel, target };
                return showMedia({ ...showMediaParams, model });
            });

        Promise
            .all(promises)
            .catch((error) => {
                window.console.log(error);
            });
    }

    componentWillUnmount() {
        this.getMediaModels().forEach((mediaModel) => {
            const target = this.convertMediaId(mediaModel.target);
            unmountMedia(target);
        });
    }

    /**
     * If feedback is in props, it means we are allowed to display the feedback. This is because the filtering of the
     * permitted feedback is done on the server side, thus the frontend can simply use everything it finds.
     * @returns {string}
     */
    getParsedFeedbackForAlternative() {
        if (this.props.feedback) {
            const element = xmlDomUtils.getElementFromString(this.props.feedback);
            return interactionParserUtils.getNodeContent(element, this.props.question.language, this.props.question.defaultLanguage);
        }
        return null;
    }

    getMediaModels() {
        const { model } = this.props;
        return Array.isArray(model.mediaModels) ? model.mediaModels : [];
    }

    convertMediaId(mediaId) {
        return `${mediaId}_feedback`;
    }

    /**
     * We'll replace the identifiers referring to the media within the interaction.content.
     * @param content Property from the interaction.
     * @returns {string}
     */
    getFixedAlternativeContent(content) {
        let fixedContent = content;
        this.getMediaModels().forEach((mediaModel) => {
            const newTarget = this.convertMediaId(mediaModel.target);
            fixedContent = fixedContent.replace(mediaModel.target, newTarget);
            fixedContent = domService.fixAudioLabelContent(fixedContent);
        });
        return fixedContent;
    }

    toggleHeader = () => {
        this.setState({
            expanded: !this.state.expanded,
        });
    }

    getHeaderClass() {
        const { checked, isCorrect } = this.props;
        const feedbackType = this.getFeedbackType();
        const isFeedbackTypeText = feedbackType === 'text' || feedbackType === 'audio';
        let headerClass = `${isFeedbackTypeText ? styles.truncate : ''} ${styles.feedbackHeader} ${styles.feedbackHeaderUnChecked}`;

        if (this.userCanViewRightWrong()) {
            headerClass = `${isFeedbackTypeText ? styles.truncate : ''}` +
                            ` ${styles.feedbackHeader} ${isCorrect ? styles.feedbackHeaderCorrect : styles.feedbackHeaderWrong}` +
                            ` ${checked ? styles.feedbackHeaderChecked : styles.feedbackHeaderUnChecked}`;
        }
        return headerClass;
    }

    getInlineFeedbackClass() {
        const feedbackType = this.getFeedbackType();
        const hasMediaModels = this.getMediaModels().length > 0;
        return `${styles.feedbackForAlternative} feedbackForAlternative ${feedbackType} ${hasMediaModels ? 'hasMediaModels' : ''}`;
    }

    getFeedbackType() {
        const { content } = this.props.model;
        const isImage = content.includes('<img');
        const isAudio = content.includes('class="Audio');
        const isLaTeX = content.includes('class="math-tex"');

        if (isImage) {
            return 'image';
        } else if (isAudio) {
            return 'audio';
        } else if (isLaTeX) {
            return 'LaTeX';
        }

        return 'text';
    }

    /**
     * If prop correctResponsesForInteraction has a value, then it means we can display correctResponses-related information.
     * No need to check for more flags, because those are guarded at higher levels.
     * @returns {boolean}
     */
    userCanViewRightWrong() {
        return !!this.props.correctResponsesForInteraction;
    }

    render() {
        const { model, checked, isCorrect } = this.props;
        const { content, identifier } = model;
        const inlineFeedback = this.getParsedFeedbackForAlternative(identifier);
        const fixedContent = this.getFixedAlternativeContent(content);
        const showOutcomeIconIndicators = this.userCanViewRightWrong();
        const feedbackType = this.getFeedbackType();
        const feedbackHeaderCaption = feedbackType !== 'image' ? t('alternativeFeedback.feedbackOn') : t('alternativeFeedback.feedback');
        const fakeFunction = () => {};

        return (
            inlineFeedback ? (<div className={this.getInlineFeedbackClass()}>
                {showOutcomeIconIndicators ?
                    <div className={`${styles.outcomeIcon}`}>
                        <OutcomeIcon
                            isCorrect={isCorrect}
                            isAttempted={checked}
                            onReady={fakeFunction}
                        />
                    </div>
                    :
                    null
                }
                <div className={this.getHeaderClass()}>
                    <button id="feedback_label" className={styles.toggle} aria-expanded={this.state.expanded} onClick={this.toggleHeader}>
                        <i className={`fa ${this.state.expanded ? 'fa-chevron-down' : 'fa-chevron-right'}`} aria-hidden />
                        <span className={`${styles.feedbackOn}`}>{feedbackHeaderCaption}</span>
                    </button>

                    {feedbackType === 'text' || feedbackType === 'LaTeX' || feedbackType === 'audio' ?
                        <div
                            dangerouslySetInnerHTML={{ __html: ` ${fixedContent} ` }}
                            className={styles.text}
                        />
                        :
                        null
                    }
                </div>
                {this.state.expanded ?
                    <div aria-labelledby="feedback_label" className={`${styles.feedbackBody}`}>
                        <React.Fragment>
                            {feedbackType === 'image' ?
                                <div className={styles.feedbackTypeImage} dangerouslySetInnerHTML={{ __html: ` ${fixedContent} ` }} />
                                :
                                null
                            }
                            <div
                                dangerouslySetInnerHTML={{ __html: ` ${inlineFeedback} ` }}
                                className="text"
                            />
                        </React.Fragment>
                    </div>
                    :
                    null
                }
            </div>) : null
        );
    }
}

FeedbackForAlternative.propTypes = {
    model: PropTypes.object, // alternative model
    question: PropTypes.object,
    feedback: PropTypes.string, // feedback for this alternative. If present, it means we can display it.
    checked: PropTypes.bool, // whether or not this alternative was chosen by the student
    isCorrect: PropTypes.bool, // whether or not this alternative is the correct one
    services: PropTypes.object,
    correctResponsesForInteraction: PropTypes.array, // Only available if when we have permission to see them in this context.
};

export default FeedbackForAlternative;
